import { useState, useCallback } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { usePlanLimits } from "../../../hooks/usePlanLimits";
import { generateCaseSummary } from "@/shared/ai";
import { supabase } from "@/shared/db";
import type {
  CaseSummaryParams,
  CaseSummaryResult,
  CaseSummaryRecord,
} from "../types/case-summary.types";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

export function useCaseSummary() {
  const { user, profile } = useAuth();
  const { checkLimit, trackUsage } = usePlanLimits();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<CaseSummaryResult | null>(null);
  const [summaries, setSummaries] = useState<CaseSummaryRecord[]>([]);
  const [summariesLoading, setSummariesLoading] = useState(false);

  /**
   * Generate AI case summary
   */
  const generateSummary = useCallback(
    async (params: CaseSummaryParams): Promise<CaseSummaryResult | null> => {
      if (!user || !profile) {
        toast.error(t("common.errors.notAuthenticated"));
        return null;
      }

      setLoading(true);
      setError(null);
      setResult(null);

      try {
        // Check plan limit
        const canGenerate = await checkLimit("case_summary");
        if (!canGenerate) {
          const msg = t("lawyer.caseSummary.limitReached");
          setError(msg);
          toast.error(msg);
          return null;
        }

        // Call AI generate function
        const summaryResult = await generateCaseSummary({
          caseTitle: params.caseTitle,
          caseDescription: params.caseDescription,
          caseNumber: params.caseNumber,
          practiceArea: params.practiceArea,
          country: profile.country || "MX",
          status: params.status,
          priority: params.priority,
          documents: params.documents || [],
          events: params.events || [],
        });

        // Save to database
        const { error: saveError } = await supabase
          .from("case_summaries")
          .insert({
            user_id: user.id,
            case_id: params.caseId || null,
            case_title: params.caseTitle,
            case_number: params.caseNumber,
            summary: summaryResult.summary,
            key_points: summaryResult.keyPoints,
            next_steps: summaryResult.nextSteps,
            timeline: summaryResult.timeline,
            recommendations: summaryResult.recommendations,
          })
          .select()
          .single();

        if (saveError) {
          console.error("Error saving summary:", saveError);
          // Continue even if save fails
        }

        // Track usage
        await trackUsage("case_summary");

        setResult(summaryResult);
        toast.success(t("lawyer.caseSummary.success"));
        return summaryResult;
      } catch (err: any) {
        console.error("Error generating summary:", err);
        const errorMessage = err.message || "Error al generar el resumen";
        setError(errorMessage);
        toast.error(errorMessage);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [user, profile, checkLimit, trackUsage]
  );

  /**
   * Fetch user's summary history
   */
  const fetchSummaries = useCallback(async () => {
    if (!user) return;

    setSummariesLoading(true);
    try {
      const { data, error: fetchError } = await supabase
        .from("case_summaries")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(20);

      if (fetchError) throw fetchError;

      setSummaries(
        (data || []).map((s) => ({
          id: s.id,
          userId: s.user_id,
          caseId: s.case_id,
          caseTitle: s.case_title,
          summary: s.summary,
          keyPoints: (Array.isArray(s.key_points)
            ? s.key_points
            : []) as string[],
          nextSteps: (Array.isArray(s.next_steps)
            ? s.next_steps
            : []) as string[],
          timeline: s.timeline,
          recommendations: (Array.isArray(s.recommendations)
            ? s.recommendations
            : []) as string[],
          createdAt: s.created_at,
        }))
      );
    } catch (err: any) {
      console.error("Error fetching summaries:", err);
    } finally {
      setSummariesLoading(false);
    }
  }, [user]);

  /**
   * Generate summary for an existing case
   */
  const generateForCase = useCallback(
    async (caseId: string): Promise<CaseSummaryResult | null> => {
      if (!user) return null;

      try {
        // Fetch case details
        const { data: caseData, error: caseError } = await supabase
          .from("cases")
          .select(
            `
            *,
            documents:case_documents(document_type, created_at, document:documents(title)),
            events:case_events(event_title, event_description, event_date, event_type)
          `
          )
          .eq("id", caseId)
          .single();

        if (caseError || !caseData) {
          toast.error("No se pudo encontrar el caso");
          return null;
        }

        const formattedDocuments = (caseData.documents || []).map(
          (doc: any) => ({
            title: doc.document?.title || "Sin título",
            document_type: doc.document_type || "pdf",
            created_at: doc.created_at,
          })
        );

        return await generateSummary({
          caseId,
          caseTitle: caseData.title,
          caseDescription: caseData.description,
          caseNumber: caseData.case_number || `CASE-${caseId.slice(0, 8)}`,
          practiceArea: caseData.practice_area || "general",
          status: caseData.status || "active",
          priority: caseData.priority || "medium",
          documents: formattedDocuments,
          events: caseData.events || [],
        });
      } catch (err: any) {
        console.error("Error generating summary for case:", err);
        toast.error("Error al generar resumen del caso");
        return null;
      }
    },
    [user, generateSummary]
  );

  /**
   * Clear current result
   */
  const clearResult = useCallback(() => {
    setResult(null);
    setError(null);
  }, []);

  return {
    // State
    loading,
    error,
    result,
    summaries,
    summariesLoading,

    // Actions
    generateSummary,
    generateForCase,
    fetchSummaries,
    clearResult,
  };
}
